import { useMemo } from "react";

/**
 * Permission gate for revealing or copying resource connection secrets.
 *
 * The UI permission check is advisory only — the API remains the
 * authorization source of truth for every secret operation. This gate
 * hides the reveal/copy affordance from users who lack the required
 * permission so they never see a secret value by default.
 *
 * Permission model (organization -> project -> secrets):
 *   - `admin` implies `write`, `deploy`, and `read`
 *   - `write` implies `read`
 *
 * Reveal/copy of a connection secret is a sensitive read operation
 * analogous to revealing a project secret. It requires at least
 * `project:${projectAlias}:write`, `project:${projectAlias}:admin`,
 * or `org:${orgAlias}:admin`. `viewer` and `developer` (without
 * project write) may not reveal or copy connection secrets.
 *
 * With cookie-based auth the JWT is httpOnly and cannot be decoded
 * client-side. The API enforces permissions on every secret operation,
 * so this gate defaults to showing the affordance and relies on the
 * API to reject unauthorized requests.
 *
 * @returns `canRevealSecret` — true when the current session may attempt
 *   project write/admin or org admin operations.
 */
export function useResourceSecretPermission(
  orgAlias: string | undefined,
  projectAlias: string | undefined,
): { canRevealSecret: boolean } {
  const canRevealSecret = useMemo(() => {
    if (!orgAlias || !projectAlias) return false;
    // The JWT is in an httpOnly cookie and cannot be decoded client-side.
    // The API is the authorization source of truth for secret operations.
    return true;
  }, [orgAlias, projectAlias]);

  return { canRevealSecret };
}
